import { useRef, useState } from "react";
import { toast } from "sonner";
import type { ApiError } from "@procureflow/shared-types";
import { fetchWithAuth } from "@/lib/auth-client";
import { UserAvatar } from "./user-avatar";

// US-082 (ADR-023) — avatar upload control. Shows the current avatar (or
// initials) and lets the user/Admin replace or remove it. Multipart PUT to
// /api/users/:id/avatar; `version` bumps after a change so the preview refreshes.

interface AvatarUploadProps {
  userId: string;
  name: string;
}

export function AvatarUpload({ userId, name }: AvatarUploadProps) {
  const [version, setVersion] = useState(1);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetchWithAuth(`/api/users/${userId}/avatar`, { method: "PUT", body: fd });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        toast.error(body?.message ?? "Upload failed.");
        return;
      }
      setVersion((v) => v + 1);
      toast.success("Photo updated.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove() {
    setBusy(true);
    try {
      const res = await fetchWithAuth(`/api/users/${userId}/avatar`, { method: "DELETE" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        toast.error(body?.message ?? "Remove failed.");
        return;
      }
      setVersion((v) => v + 1);
      toast.success("Photo removed.");
    } catch {
      toast.error("Remove failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      <UserAvatar userId={userId} name={name} size={48} version={version} />
      <div className="flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          aria-label="Upload photo"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
          }}
          className="text-xs text-muted-foreground file:mr-2 file:rounded-md file:border file:border-border file:bg-background file:px-2 file:py-1 file:text-xs file:font-medium hover:file:bg-muted"
        />
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy}
          className="self-start text-xs text-muted-foreground hover:text-destructive disabled:opacity-50"
        >
          Remove photo
        </button>
      </div>
    </div>
  );
}
